import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { format, isSameDay, isBefore, startOfDay, addDays, subDays, isWithinInterval } from "date-fns";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Truck, MapPin, Info, Loader2, Heart, Play, Star, Ruler, ChevronDown, ArrowLeft, Sparkles, Check, X } from "lucide-react";
import { Calendar } from "../components/ui/calendar";
import { Button } from "../components/ui/button";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CompleteTheLook from "../components/CompleteTheLook";
import CTLQuickView from "../components/CTLQuickView";
import { addToRecentlyViewed } from "../components/RecentlyViewed";
import { trackEvent, gtagAddToCart } from "../lib/analytics";
import BundleSummary from "../components/BundleSummary";
import UpsellPopup from "../components/UpsellPopup";
import FrequentlyBundled from "../components/FrequentlyBundled";
import HorizontalProductCarousel from "../components/HorizontalProductCarousel";
import SizeGuideModal from "../components/SizeGuideModal";
import ReviewsTabs from "../components/ReviewsTabs";
import SaveHeart from "../components/SaveHeart";
import SEO from "../components/SEO";
import TierDiscountStrip from "../components/TierDiscountStrip";
import { useEventDate } from "../contexts/BookingContext";
import { useCart } from "../contexts/CartContext";
import { useBuildLook, LOOK_SLOTS } from "../contexts/BuildLookContext";
import { useDeliveryConfig } from "../contexts/SiteTextsContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Collapsible section component
function CollapsibleSection({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-[#282828]/8">
      <button onClick={() => setOpen(!open)} className="flex items-center justify-between w-full py-4 text-left" data-testid={`collapsible-${title.toLowerCase().replace(/\s+/g,'-')}`}>
        <span className="text-[13px] text-[#282828] font-medium tracking-[0.03em]">{title}</span>
        <ChevronDown className={`w-4 h-4 text-[#676664]/40 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} strokeWidth={1.5} />
      </button>
      <div className={`overflow-hidden transition-all duration-200 ${open ? 'max-h-[500px] pb-4' : 'max-h-0'}`}>
        {children}
      </div>
    </div>
  );
}

function YouMayAlsoLikeCarousel({ outfitId }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!outfitId) return;
    axios.get(`${API}/outfits/${outfitId}/similar?limit=12`)
      .then(res => setItems(res.data.outfits || []))
      .catch(() => {});
  }, [outfitId]);
  return <HorizontalProductCarousel title="You May Also Like" items={items} testId="you-may-also-like" />;
}

function RecentlyViewedCarousel({ currentOutfitId }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    try {
      const viewed = JSON.parse(localStorage.getItem("recentlyViewed") || "[]")
        .filter(v => v.id !== currentOutfitId).slice(0, 12);
      setItems(viewed);
    } catch { /* silent */ }
  }, [currentOutfitId]);
  return <HorizontalProductCarousel title="Recently Viewed" items={items} testId="recently-viewed" />;
}

export default function OutfitDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { eventDate, setEventDate, rentalDuration, setRentalDuration, setShowPicker } = useEventDate();
  const { addToCart, updateCartItem, fetchCart, cartGroups } = useCart();
  const { 
    isActive: isBuildLookMode, 
    activeSlot, 
    addToLook, 
    lockEventDate, 
    lockRentalPeriod,
    lockDeliveryMethod,
    eventDate: buildLookEventDate,
    rentalPeriod: buildLookRentalPeriod,
    slots: buildLookSlots,
  } = useBuildLook();
  const { auspost_content_visible } = useDeliveryConfig();
  
  // Check if we're in Build Your Look mode from URL params
  const buildLookParam = searchParams.get("buildLook") === "true";
  const slotParam = searchParams.get("slot");
  const fromBuildLook = searchParams.get("from") === "build-look";
  const inBuildLookFlow = buildLookParam;
  const targetSlot = slotParam || activeSlot;
  
  const [outfit, setOutfit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookedDates, setBookedDates] = useState([]);
  const [dateInfo, setDateInfo] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedRentalOption, setSelectedRentalOption] = useState(null);
  const [selectedRentalOptions, setSelectedRentalOptions] = useState([]);
  const [localRentalDays, setLocalRentalDays] = useState(rentalDuration || 4);
  const [bundleItems, setBundleItems] = useState([]);
  const [featureToggles, setFeatureToggles] = useState(null);
  const [showUpsell, setShowUpsell] = useState(false);
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [reviews, setReviews] = useState({ reviews: [], average_rating: 0, count: 0 });
  const [deliveryMethod, setDeliveryMethod] = useState("pickup");
  const [quickViewOutfit, setQuickViewOutfit] = useState(null);
  const [lockedBooking, setLockedBooking] = useState(null);
  const [categoryTree, setCategoryTree] = useState([]);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [lookItems, setLookItems] = useState([]);
  const [selectedLookIds, setSelectedLookIds] = useState(new Set());
  const [lookLoading, setLookLoading] = useState(true);
  const [showDateChange, setShowDateChange] = useState(false);
  const [cartItemMatch, setCartItemMatch] = useState(null); // existing cart item being edited
  const [showReplaceDialog, setShowReplaceDialog] = useState(false); // "Replace or Add New?" dialog
  const [resolvedOutfitId, setResolvedOutfitId] = useState(null); // UUID resolved from slug
  // Section 5 Rev 2 — same-day / next-day event dates are pickup-only.
  // Evaluated in Melbourne so DST + timezone drift never allows delivery to
  // slip through. Mirrors backend `_is_same_or_next_day_melbourne`.
  const isPickupOnlyDate = (() => {
    if (!eventDate) return false;
    try {
      const melTodayStr = new Date().toLocaleString("en-CA", { timeZone: "Australia/Melbourne", year: "numeric", month: "2-digit", day: "2-digit" });
      const melToday = new Date(`${melTodayStr}T00:00:00`);
      const evStart = new Date(eventDate); evStart.setHours(0, 0, 0, 0);
      const diff = Math.round((evStart - melToday) / (1000 * 60 * 60 * 24));
      return diff >= 0 && diff <= 1;
    } catch {
      return false;
    }
  })();
  const deliveryFee = deliveryMethod === "delivery" ? 15 : 0;
  // Customer-visible dates: Delivery = Event - 1, Rental End = Delivery + Duration - 1
  const deliveryDate = eventDate ? subDays(eventDate, 1) : null;
  const rentalEnd = deliveryDate && localRentalDays ? addDays(deliveryDate, localRentalDays - 1) : null;
  // Return window: last 2 days of visible rental
  const returnWindowStart = rentalEnd ? subDays(rentalEnd, 1) : null;
  const returnWindowEnd = rentalEnd;

  useEffect(() => { fetchOutfit(); fetchFeatureToggles(); fetchCategoryTree(); }, [id]);
  useEffect(() => {
    // Check for locked booking params from CTL "View More"
    const locked = sessionStorage.getItem("mahra_locked_booking");
    if (locked) {
      try {
        const params = JSON.parse(locked);
        setLockedBooking(params);
        if (params.eventDate) setEventDate(new Date(params.eventDate));
        if (params.duration) { setLocalRentalDays(params.duration); setRentalDuration(params.duration); }
        if (params.deliveryMethod) setDeliveryMethod(params.deliveryMethod);
        sessionStorage.removeItem("mahra_locked_booking");
      } catch { sessionStorage.removeItem("mahra_locked_booking"); }
    } else if (!inBuildLookFlow) {
      // Restore PDP selections from sessionStorage (e.g. when navigating back from cart)
      const saved = sessionStorage.getItem(`mahra_pdp_state_${id}`);
      if (saved) {
        try {
          const s = JSON.parse(saved);
          if (s.eventDate) setEventDate(new Date(s.eventDate));
          if (s.localRentalDays) { setLocalRentalDays(s.localRentalDays); setRentalDuration(s.localRentalDays); }
          if (s.deliveryMethod) setDeliveryMethod(s.deliveryMethod);
          if (s.selectedRentalOption) setSelectedRentalOption(s.selectedRentalOption);
          if (s.selectedRentalOptions) setSelectedRentalOptions(s.selectedRentalOptions);
          if (s.scrollY) setTimeout(() => window.scrollTo(0, s.scrollY), 300);
        } catch { /* silent */ }
      }
    }
  }, [id]);
  
  // Persist PDP selections to sessionStorage for back-button state restoration
  useEffect(() => {
    if (!id || inBuildLookFlow || lockedBooking) return;
    const state = {
      eventDate: eventDate ? eventDate.toISOString() : null,
      localRentalDays,
      deliveryMethod,
      selectedRentalOption,
      selectedRentalOptions,
      scrollY: window.scrollY,
    };
    sessionStorage.setItem(`mahra_pdp_state_${id}`, JSON.stringify(state));
  }, [id, eventDate, localRentalDays, deliveryMethod, selectedRentalOption, selectedRentalOptions, inBuildLookFlow, lockedBooking]);

  // Detect if this product exists in the cart (for "Update Cart" mode)
  const cartEditParam = searchParams.get("cartEdit"); // cart item id passed from cart edit flow
  useEffect(() => {
    if (inBuildLookFlow || !id) return;
    const detect = async () => {
      const data = await fetchCart();
      if (!data?.groups) return;
      const allItems = data.groups.flatMap(g => [g.main, ...g.addons]);
      // If opened from cart edit link, match by cart item id
      if (cartEditParam) {
        const match = allItems.find(i => i.id === cartEditParam);
        if (match) {
          setCartItemMatch(match);
          setEventDate(new Date(match.event_date));
          setLocalRentalDays(match.duration);
          setRentalDuration(match.duration);
          setDeliveryMethod(match.delivery_method || "pickup");
          return;
        }
      }
      // Otherwise check if any cart item matches this outfit
      const matches = allItems.filter(i => i.outfit_id === id);
      if (matches.length > 0) {
        setCartItemMatch(matches[0]);
      }
    };
    detect();
  }, [id, inBuildLookFlow, cartEditParam]);
  useEffect(() => { if (outfit && resolvedOutfitId) { fetchAvailability(); fetchReviews(); } }, [outfit, resolvedOutfitId, currentMonth]);

  // Batch 9 — auto-scroll + auto-open review form when the URL hash or
  // ?review=1 query is present (from review-request email link).
  useEffect(() => {
    if (!outfit) return;
    const hash = (typeof window !== "undefined" && window.location.hash) || "";
    const search = (typeof window !== "undefined" && window.location.search) || "";
    if (!/(^|[#?&])review(=|$)/i.test(hash + search)) return;
    const tryScroll = (attempt = 0) => {
      const el = document.querySelector('[data-testid="reviews-tabs"], [data-testid="reviews-tabs-business-only"], [data-testid="review-section"]');
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        // Auto-click "Write a Review" so the form is immediately open.
        setTimeout(() => {
          const btn = document.querySelector('[data-testid="write-review-btn"]');
          if (btn) btn.click();
        }, 600);
      } else if (attempt < 10) {
        setTimeout(() => tryScroll(attempt + 1), 250);
      }
    };
    setTimeout(() => tryScroll(0), 800);
  }, [outfit]);
  useEffect(() => {
    if (eventDate) {
      setLocalRentalDays(rentalDuration || 4);
      calculateBlockedDates(eventDate, rentalDuration || 4);
    } else { setDateInfo(null); }
  }, [eventDate, rentalDuration]);
  useEffect(() => { if (deliveryDate) setCurrentMonth(deliveryDate); }, [eventDate]);

  // Section 5 Rev 2 — auto-switch to pickup whenever the selected event date is
  // today or tomorrow (Melbourne). Same-day / next-day is pickup-only.
  useEffect(() => {
    if (isPickupOnlyDate && deliveryMethod !== "pickup" && !lockedBooking) {
      setDeliveryMethod("pickup");
    }
  }, [isPickupOnlyDate, deliveryMethod, lockedBooking]);

  // Show sticky bar on mobile when scrolled past the booking section
  useEffect(() => {
    const onScroll = () => setShowStickyBar(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Auto-set dates from Build Look context when adding accessories
  const isBuildLookAccessory = inBuildLookFlow && targetSlot && targetSlot !== "outfit" && buildLookEventDate;
  useEffect(() => {
    if (isBuildLookAccessory) {
      const d = new Date(buildLookEventDate + "T00:00:00");
      setEventDate(d);
      if (buildLookRentalPeriod) {
        setLocalRentalDays(buildLookRentalPeriod);
        setRentalDuration(buildLookRentalPeriod);
      }
    }
  }, [isBuildLookAccessory, buildLookEventDate, buildLookRentalPeriod]);

  // Fetch AI look recommendations
  useEffect(() => {
    if (!id) return;
    setLookLoading(true);
    setSelectedLookIds(new Set());
    fetch(`${API}/ai/recommendations/${id}`)
      .then(r => r.json())
      .then(d => setLookItems(d.build_the_look || []))
      .catch(() => {})
      .finally(() => setLookLoading(false));
  }, [id]);

  const fetchOutfit = async () => {
    try {
      setLoading(true);
      let outfitId = id;
      // If id is a slug (not a UUID), resolve it
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(id);
      if (!isUUID) {
        const slugRes = await axios.get(`${API}/outfit-by-slug/${id}`);
        outfitId = slugRes.data.id;
      }
      setResolvedOutfitId(outfitId);
      const response = await axios.get(`${API}/outfits/${outfitId}`);
      const data = response.data;
      setOutfit(data);
      addToRecentlyViewed(data);
      trackEvent("product_view", outfitId);
      // If URL uses UUID and we have a slug, redirect to slug URL
      if (isUUID && data.slug) {
        const params = searchParams.toString();
        navigate(`/outfit/${data.slug}${params ? `?${params}` : ""}`, { replace: true });
      }
      if (data.rental_options?.length > 0) {
        if (data.variation_selection_mode === "multiple") {
          setSelectedRentalOptions([]);
          setSelectedRentalOption(null);
        } else {
          setSelectedRentalOption(data.rental_options[0]);
          setSelectedRentalOptions([]);
        }
      }
    } catch (error) { toast.error("Piece not found"); navigate("/catalog"); } finally { setLoading(false); }
  };
  const fetchFeatureToggles = async () => { try { const res = await axios.get(`${API}/settings/feature-toggles`); setFeatureToggles(res.data.toggles); } catch {} };
  const fetchCategoryTree = async () => { try { const res = await axios.get(`${API}/settings/categories`); setCategoryTree(res.data.categories || []); } catch {} };
  const fetchReviews = async () => { if (!resolvedOutfitId) return; try { const res = await axios.get(`${API}/outfits/${resolvedOutfitId}/reviews`); setReviews(res.data); } catch {} };
  const fetchAvailability = async () => {
    if (!resolvedOutfitId) return;
    try {
      const response = await axios.get(`${API}/outfits/${resolvedOutfitId}/availability?month=${currentMonth.getMonth() + 1}&year=${currentMonth.getFullYear()}`);
      setBookedDates(response.data.booked_dates.map(d => new Date(d + "T00:00:00")));
    } catch (error) { console.error("Error:", error); }
  };
  const formatLocalDate = (date) => { const y = date.getFullYear(); const m = String(date.getMonth() + 1).padStart(2, '0'); const d = String(date.getDate()).padStart(2, '0'); return `${y}-${m}-${d}`; };
  const calculateBlockedDates = async (date, days) => {
    if (!date) return;
    try { const dateStr = formatLocalDate(date); const response = await axios.get(`${API}/booking/calculate-dates?event_date=${dateStr}&rental_days=${days}`); setDateInfo(response.data); } catch (error) { console.error("Error:", error); }
  };
  // Handle event date selection on PDP calendar
  const handleEventDateSelect = (date) => {
    if (!date) { setEventDate(null); setDateInfo(null); return; }
    // Check conflicts using backend blocking range (Event-2 to rental_end+2)
    const blockedStart = subDays(date, 2);
    const rentalEndDate = addDays(subDays(date, 1), localRentalDays - 1);
    const blockedEndDate = addDays(rentalEndDate, 2);
    const blockedRange = [];
    let current = new Date(blockedStart);
    while (current <= blockedEndDate) { blockedRange.push(new Date(current)); current = addDays(current, 1); }
    const hasConflict = blockedRange.some(d => bookedDates.some(booked => isSameDay(booked, d)));
    if (hasConflict) { toast.error("This date conflicts with existing bookings."); return; }
    setEventDate(date); setRentalDuration(localRentalDays);
  };
  const handleRentalDaysChange = (newDays) => { setLocalRentalDays(newDays); setRentalDuration(newDays); if (eventDate) calculateBlockedDates(eventDate, newDays); };
  const isDateDisabled = (date) => { if (isBefore(date, startOfDay(new Date()))) return true; return bookedDates.some(d => isSameDay(d, date)); };
  // Highlight the visible rental range: Delivery (Event-1) to Rental End
  const isInRentalRange = (date) => {
    if (!deliveryDate || !rentalEnd) return false;
    return isWithinInterval(date, { start: startOfDay(deliveryDate), end: startOfDay(rentalEnd) });
  };

  const calculateCost = () => {
    if (!outfit) return null;
    const isMultiMode = outfit.variation_selection_mode === "multiple";
    let rentalCost;

    if (isMultiMode && selectedRentalOptions.length > 0) {
      // Sum up prices of all selected options
      rentalCost = selectedRentalOptions.reduce((sum, opt) => {
        let optPrice;
        if (localRentalDays <= 4 && opt.price_4_day > 0) optPrice = opt.price_4_day;
        else if (localRentalDays > 4 && opt.price_8_day > 0) optPrice = opt.price_8_day;
        else optPrice = opt.price_4_day || opt.price_per_day || 0;
        return sum + optPrice;
      }, 0);
    } else if (selectedRentalOption) {
      if (localRentalDays <= 4 && selectedRentalOption.price_4_day > 0) rentalCost = selectedRentalOption.price_4_day;
      else if (localRentalDays > 4 && selectedRentalOption.price_8_day > 0) rentalCost = selectedRentalOption.price_8_day;
      else rentalCost = selectedRentalOption.price_4_day || selectedRentalOption.price_per_day || outfit.price_4_day || 0;
    } else {
      if (localRentalDays <= 4 && outfit.price_4_day > 0) rentalCost = outfit.price_4_day;
      else if (localRentalDays > 4 && outfit.price_8_day > 0) rentalCost = outfit.price_8_day;
      else rentalCost = outfit.price_4_day || outfit.price_per_day || 0;
    }
    let promoDiscount = 0;
    if (outfit.promotion_type === "percentage" && outfit.promotion_value > 0) promoDiscount = Math.round(rentalCost * outfit.promotion_value / 100 * 100) / 100;
    else if (outfit.promotion_type === "fixed" && outfit.promotion_value > 0) promoDiscount = Math.min(outfit.promotion_value, rentalCost);
    rentalCost = Math.max(0, rentalCost - promoDiscount);
    // Single source of truth: variation-aware bond resolver also used by the top Security Bond display.
    // Pass post-promo rentalCost so single-select percentage bonds compute against the same base.
    const deposit = getBondAmount(rentalCost);
    return { rentalCost, deposit, total: rentalCost + deposit + deliveryFee, promoDiscount, deliveryFee };
  };

  const handleAddBundle = useCallback((item, added) => { setBundleItems(prev => { if (added) return [...prev.filter(b => b.id !== item.id), item]; return prev.filter(b => b.id !== item.id); }); }, []);
  const handleRemoveBundle = useCallback((itemId) => { setBundleItems(prev => prev.filter(b => b.id !== itemId)); }, []);
  const handleUpsellAdd = useCallback((items) => { setBundleItems(prev => { const existingIds = new Set(prev.map(b => b.id)); const newItems = items.filter(i => !existingIds.has(i.id)); return [...prev, ...newItems]; }); }, []);

  // Handle Add To Look for Build Your Look flow
  const handleAddToLook = () => {
    if (!outfit) return;
    
    // For outfit slot, we need event date and rental period
    if (targetSlot === "outfit") {
      if (!eventDate) {
        setShowPicker(true);
        return;
      }
      // Lock in the dates and delivery method for the look
      lockEventDate(formatLocalDate(eventDate));
      lockRentalPeriod(localRentalDays);
      lockDeliveryMethod(deliveryMethod);
    }
    
    // If dates were changed in accessory mode, update the look context
    if (targetSlot !== "outfit" && showDateChange && eventDate) {
      lockEventDate(formatLocalDate(eventDate));
      lockRentalPeriod(localRentalDays);
      lockDeliveryMethod(deliveryMethod);
    }
    
    // Create the product object for the look.
    //
    // NOTE: BuildLookContext's duplicate-check keys on `rental_option_id`.
    // We deliberately write BOTH the canonical names (rental_option_id /
    // rental_option_name) and the legacy aliases (selected_option_id /
    // selected_option_name) so any code paths still reading the legacy names
    // keep working. The context normalises before comparing.
    const _optId = selectedRentalOption?.id || null;
    const _optName = selectedRentalOption?.name || null;
    // Prefer the variation's own image when one is set, otherwise fall back to
    // the product's hero image. This keeps the BYL / cart / look summary
    // visually consistent with what the customer just chose.
    const _heroImage = selectedRentalOption?.image || outfit.images?.[0] || null;
    const productForLook = {
      id: outfit.id,
      name: outfit.name,
      images: outfit.images,
      image: _heroImage,
      rental_price: cost?.rentalCost || outfit.price_4_day || 0,
      bond_amount: getBondAmount() || outfit.deposit || 0,
      rental_option_id: _optId,
      rental_option_name: _optName,
      rental_option_image: selectedRentalOption?.image || null,
      selected_option_id: _optId,
      selected_option_name: _optName,
      category: outfit.category,
    };
    
    addToLook(productForLook, targetSlot);
    trackEvent("add_to_look", outfit.id);
  };

  const handleAddToCart = async () => {
    if (!eventDate) { setShowPicker(true); return; }
    const isMultiMode = outfit.variation_selection_mode === "multiple";
    if (outfit.rental_options?.length > 0 && !isMultiMode && !selectedRentalOption) { toast.error("Please select a rental option"); return; }
    if (outfit.rental_options?.length > 0 && isMultiMode && selectedRentalOptions.length === 0) { toast.error("Please select at least one rental option"); return; }

    const isAddonPage = !!lockedBooking;
    const isUpdateMode = !!cartItemMatch && !isAddonPage;

    // Check if user changed dates/duration from the cart item — ask replace or add new
    if (isUpdateMode && !showReplaceDialog) {
      const originalDate = cartItemMatch.event_date?.slice(0, 10);
      const currentDate = formatLocalDate(eventDate);
      const datesChanged = originalDate !== currentDate || cartItemMatch.duration !== localRentalDays;
      if (datesChanged) {
        setShowReplaceDialog(true);
        return; // dialog will call handleUpdateCart or handleAddNew
      }
    }

    if (isUpdateMode) {
      return handleUpdateCart();
    }

    // Section 5 Rev 2 — belt-and-braces: even if the auto-switch effect hasn't
    // run yet (state batching / rapid clicks), coerce to pickup for same-day /
    // next-day bookings before hitting the API.
    const effectiveDeliveryMethod = isPickupOnlyDate ? "pickup" : deliveryMethod;

    const cartItem = {
      outfit_id: outfit.id,
      event_date: formatLocalDate(eventDate),
      duration: localRentalDays,
      delivery_method: effectiveDeliveryMethod,
      rental_option_id: isMultiMode ? (selectedRentalOptions.map(o => o.id).join(",")) : (selectedRentalOption?.id || null),
      rental_option_name: isMultiMode ? (selectedRentalOptions.map(o => o.name).join(" + ")) : (selectedRentalOption?.name || null),
      is_addon: isAddonPage,
      parent_cart_item_id: null,
      parent_outfit_id: isAddonPage ? (lockedBooking.parentOutfitId || null) : null,
    };

    const result = await addToCart(cartItem);
    // Section 5 Rev 2 — CartContext already surfaces the specific server-side
    // error message (e.g. pickup-only guard) via toast. Only fall back to a
    // generic message when there was no response at all.
    if (!result) { return; }
    // Fire Google Ads / GA4 add_to_cart
    const itemPrice = localRentalDays === 8 ? (selectedRentalOption?.price_8_day || outfit.price_8_day || 0) : (selectedRentalOption?.price_4_day || outfit.price_4_day || 0);
    gtagAddToCart(itemPrice, outfit.name);
    // Clear saved PDP state after successful add
    sessionStorage.removeItem(`mahra_pdp_state_${id}`);

    // If main item, also add bundle items as add-ons
    if (!isAddonPage && bundleItems.length > 0) {
      for (const b of bundleItems) {
        await addToCart({
          outfit_id: b.id,
          event_date: formatLocalDate(eventDate),
          duration: localRentalDays,
          delivery_method: effectiveDeliveryMethod,
          rental_option_id: b.bundleOption?.id || null,
          rental_option_name: b.bundleOption?.name || null,
          is_addon: true,
          parent_cart_item_id: result.item.id,
          parent_outfit_id: outfit.id,
        });
      }
    }
    // Add selected Build Your Look items as add-ons
    if (!isAddonPage && selectedLookIds.size > 0) {
      const selectedLooks = lookItems.filter(i => selectedLookIds.has(i.id));
      for (const item of selectedLooks) {
        await addToCart({
          outfit_id: item.id,
          event_date: formatLocalDate(eventDate),
          duration: localRentalDays,
          delivery_method: effectiveDeliveryMethod,
          rental_option_id: null,
          rental_option_name: null,
          is_addon: true,
          parent_cart_item_id: result.item.id,
          parent_outfit_id: outfit.id,
        });
      }
    }
    // Confirmation panel auto-shown by CartContext
  };

  const handleUpdateCart = async () => {
    if (!cartItemMatch) return;
    const isMultiMode = outfit?.variation_selection_mode === "multiple";
    const updates = {
      event_date: formatLocalDate(eventDate),
      duration: localRentalDays,
      delivery_method: deliveryMethod,
      rental_option_id: isMultiMode ? (selectedRentalOptions.map(o => o.id).join(",")) : (selectedRentalOption?.id || null),
      rental_option_name: isMultiMode ? (selectedRentalOptions.map(o => o.name).join(" + ")) : (selectedRentalOption?.name || null),
    };
    const result = await updateCartItem(cartItemMatch.id, updates);
    if (result) {
      toast.success("Cart updated");
      sessionStorage.removeItem(`mahra_pdp_state_${id}`);
      setShowReplaceDialog(false);
      // Refresh cart match with updated data
      setCartItemMatch({ ...cartItemMatch, ...updates });
      await fetchCart();
    } else {
      toast.error("Failed to update cart");
    }
  };

  const handleAddNew = async () => {
    setShowReplaceDialog(false);
    setCartItemMatch(null); // temporarily clear so addToCart path runs
    const isMultiMode = outfit?.variation_selection_mode === "multiple";
    const cartItem = {
      outfit_id: outfit.id,
      event_date: formatLocalDate(eventDate),
      duration: localRentalDays,
      delivery_method: deliveryMethod,
      rental_option_id: isMultiMode ? (selectedRentalOptions.map(o => o.id).join(",")) : (selectedRentalOption?.id || null),
      rental_option_name: isMultiMode ? (selectedRentalOptions.map(o => o.name).join(" + ")) : (selectedRentalOption?.name || null),
      is_addon: false,
      parent_cart_item_id: null,
      parent_outfit_id: null,
    };
    const result = await addToCart(cartItem);
    if (result) {
      toast.success("Added as a new booking");
      sessionStorage.removeItem(`mahra_pdp_state_${id}`);
    }
    // Section 5 Rev 2 — on failure CartContext already toasts the server detail.
  };

  const getPriceForDuration = (days) => {
    const isMultiMode = outfit?.variation_selection_mode === "multiple";
    if (isMultiMode && selectedRentalOptions.length > 0) {
      return selectedRentalOptions.reduce((sum, opt) => {
        if (days <= 4 && opt.price_4_day > 0) return sum + opt.price_4_day;
        if (days > 4 && opt.price_8_day > 0) return sum + opt.price_8_day;
        return sum + (opt.price_4_day || opt.price_per_day || 0);
      }, 0);
    }
    const opt = selectedRentalOption;
    if (opt) { if (days <= 4 && opt.price_4_day > 0) return opt.price_4_day; if (days > 4 && opt.price_8_day > 0) return opt.price_8_day; return opt.price_4_day || opt.price_per_day || 0; }
    if (days <= 4 && outfit.price_4_day > 0) return outfit.price_4_day; if (days > 4 && outfit.price_8_day > 0) return outfit.price_8_day; return outfit.price_4_day || outfit.price_per_day || 0;
  };
  /**
   * Resolve the security bond shown on the product page.
   *
   * Order of precedence:
   *   1. Multi-select mode → SUM the bond of every selected variation that
   *      has its own bond_value > 0; any variation in the selection without
   *      a bond contributes the product-level fallback once (so a mixed
   *      selection still surfaces the right total). Variation bond_type
   *      "percentage" is honoured against that variation's rental price.
   *   2. Single-select mode with a selected variation that declares its own
   *      bond_value > 0 → use the variation bond (percentage honoured).
   *   3. Otherwise → fall back to the product-level bond exactly as before.
   *
   * Mirrors `_resolve_variation_bond()` in backend/server.py so the page
   * displays the same number the cart will charge.
   */
  const getBondAmount = (singleSelectRentalCost) => {
    if (!outfit) return 0;

    const bondForOption = (opt, rentalPriceForOpt) => {
      const v = opt && opt.bond_value;
      if (v !== undefined && v !== null && v !== "" && Number(v) > 0) {
        const t = opt.bond_type || outfit.bond_type || "fixed";
        return t === "percentage" ? Math.round((rentalPriceForOpt || 0) * Number(v) / 100) : Number(v);
      }
      // Product-level fallback (preserved byte-identically)
      if (outfit.bond_type === "percentage" && outfit.bond_value > 0) {
        return Math.round((rentalPriceForOpt || getPriceForDuration(localRentalDays)) * outfit.bond_value / 100);
      }
      return outfit.bond_value || outfit.deposit_amount || 0;
    };

    const isMultiMode = outfit.variation_selection_mode === "multiple";

    // 1. Multi-select: sum per-variation bond resolutions
    if (isMultiMode && Array.isArray(selectedRentalOptions) && selectedRentalOptions.length > 0) {
      return selectedRentalOptions.reduce((sum, opt) => {
        const priceForOpt = (localRentalDays <= 4 ? opt.price_4_day : opt.price_8_day) || opt.price_4_day || 0;
        return sum + bondForOption(opt, priceForOpt);
      }, 0);
    }

    // 2. Single-select: prefer the selected variation's bond.
    //    Use caller-supplied rentalCost (post-promo) when provided so the Booking Summary
    //    percentage-bond calculation matches the same rentalCost shown in the summary.
    if (selectedRentalOption) {
      const basePrice = (typeof singleSelectRentalCost === "number" && singleSelectRentalCost >= 0)
        ? singleSelectRentalCost
        : getPriceForDuration(localRentalDays);
      return bondForOption(selectedRentalOption, basePrice);
    }

    // 3. No variation chosen → product-level fallback (original behaviour)
    const basePrice = (typeof singleSelectRentalCost === "number" && singleSelectRentalCost >= 0)
      ? singleSelectRentalCost
      : getPriceForDuration(localRentalDays);
    if (outfit.bond_type === "percentage" && outfit.bond_value > 0) {
      return Math.round(basePrice * outfit.bond_value / 100);
    }
    return outfit.bond_value || outfit.deposit_amount || 0;
  };

  const toggleLookItem = (item) => {
    setSelectedLookIds(prev => {
      const next = new Set(prev);
      next.has(item.id) ? next.delete(item.id) : next.add(item.id);
      return next;
    });
  };

  const getLookCost = () => {
    const selected = lookItems.filter(i => selectedLookIds.has(i.id));
    if (selected.length === 0) return { subtotal: 0, discount: 0, discountPct: 0, total: 0, bonds: 0 };
    const subtotal = selected.reduce((sum, i) => {
      const price = localRentalDays <= 4 ? (i.price_4_day || 0) : (i.price_8_day || i.price_4_day || 0);
      return sum + price;
    }, 0);
    const bonds = selected.reduce((sum, i) => {
      if (i.bond_type === "percentage" && i.bond_value > 0) {
        const price = localRentalDays <= 4 ? (i.price_4_day || 0) : (i.price_8_day || i.price_4_day || 0);
        return sum + Math.round(price * i.bond_value / 100);
      }
      return sum + (i.bond_value || 0);
    }, 0);
    const totalItems = selected.length + 1;
    const discountPct = totalItems >= 4 ? 20 : totalItems === 3 ? 15 : totalItems === 2 ? 10 : 0;
    const discount = Math.round(subtotal * discountPct / 100);
    return { subtotal, discount, discountPct, total: subtotal - discount, bonds };
  };

  // ──────────────────────────────────────────────────────────────────────
  // Variation-specific hero image override.
  // MUST stay above any conditional `return` below (Rules of Hooks: hook
  // count must be stable between renders, otherwise React error #310).
  // When the customer picks a different variation that has its own image,
  // jump the gallery back to the variation hero (index 0). Variations
  // without an image leave the current thumbnail position untouched.
  // ──────────────────────────────────────────────────────────────────────
  const variationImage = selectedRentalOption?.image || null;
  useEffect(() => {
    if (variationImage) setCurrentImageIndex(0);
  }, [variationImage]);

  if (loading) return <div className="min-h-screen bg-[#FAF8F0] flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-[#9A7B4F]" /></div>;
  if (!outfit) return null;

  const cost = calculateCost();
  const lookCost = getLookCost();
  const combinedTotal = cost ? cost.total + lookCost.total + lookCost.bonds : 0;
  const images = outfit.images?.length > 0 ? outfit.images : ["https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=800"];
  const videos = outfit.videos || [];

  // Build the gallery — when the variation has its own image, prepend it
  // so it becomes the hero. Filter it out of the secondary list to avoid
  // showing the same picture twice in the thumbnails.
  const allMedia = [
    ...(variationImage ? [{ type: 'image', url: variationImage, _variation: true }] : []),
    ...images.filter(url => url !== variationImage).map(url => ({ type: 'image', url })),
    ...videos.map(url => ({ type: 'video', url })),
  ];

  const isYouTubeUrl = (url) => url.includes('youtube.com') || url.includes('youtu.be');
  const getYouTubeEmbedUrl = (url) => { const videoId = url.includes('youtu.be') ? url.split('/').pop() : url.split('v=')[1]?.split('&')[0]; return `https://www.youtube.com/embed/${videoId}`; };

  // Resolve breadcrumb from category tree
  const breadcrumb = (() => {
    if (!outfit || !categoryTree.length) return null;
    const outfitCategory = outfit.category;
    for (const parent of categoryTree) {
      if (parent.enabled === false) continue;
      const children = (parent.children || []).filter(c => c.enabled !== false);
      const matchedChild = children.find(c => c.name === outfitCategory);
      if (matchedChild) {
        return {
          parent: { name: parent.name, url: `/${parent.slug || parent.id}` },
          child: { name: matchedChild.name, url: `/${parent.slug || parent.id}/${matchedChild.slug || matchedChild.id}` },
        };
      }
      if (parent.name === outfitCategory) {
        return { parent: { name: parent.name, url: `/${parent.slug || parent.id}` }, child: null };
      }
    }
    return null;
  })();

  // Build SEO data
  const lowestPrice = outfit ? (() => {
    const prices = [outfit.price_4_day, outfit.price_8_day, ...(outfit.rental_options || []).flatMap(o => [o.price_4_day, o.price_8_day])].filter(p => p > 0);
    return prices.length > 0 ? Math.min(...prices) : 0;
  })() : 0;

  const seoCategory = outfit ? (outfit.category || "outfit").toLowerCase() : "outfit";
  const outfitAlt = outfit ? `${outfit.name} for rent in Melbourne – Indian ${seoCategory} hire` : "";

  const seoBreadcrumbs = outfit ? [
    { name: "Home", url: "/" },
    ...(breadcrumb ? [{ name: breadcrumb.parent.name, url: breadcrumb.parent.url }] : []),
    { name: outfit.name },
  ] : [];

  return (
    <div className="min-h-screen bg-[#FAF8F0] relative overflow-x-hidden w-full">
      {outfit && (
        <SEO
          title={`Rent ${outfit.name} in Melbourne – Indian ${seoCategory.charAt(0).toUpperCase() + seoCategory.slice(1)} Hire`}
          description={`Rent ${outfit.name} for your next event in Melbourne. ${outfit.occasion ? `Perfect for ${outfit.occasion}. ` : ""}From $${lowestPrice}/rental. Premium Indian ${seoCategory} hire with free local pickup.`}
          path={`/outfit/${outfit.slug || id}`}
          image={outfit.images?.[0]}
          type="product"
          product={{
            name: outfit.name,
            price: lowestPrice,
            images: outfit.images || [],
            description: outfit.description || `Rent ${outfit.name} – premium Indian ${seoCategory} for hire in Melbourne`,
            available: outfit.available,
            category: outfit.category_slug || "",
          }}
          breadcrumbs={seoBreadcrumbs}
        />
      )}
      <div className="petal-left hidden lg:block" />
      <div className="petal-right hidden lg:block" />
      <Navbar />
      <TierDiscountStrip />
      <div className="max-w-[1400px] mx-auto px-4 lg:px-10 pt-4 pb-6 lg:py-12 overflow-hidden w-full box-border">
        <nav className="flex items-center text-[11px] text-[#676664]/40 mb-4 lg:mb-8" data-testid="pdp-breadcrumb">
          <button onClick={() => navigate(-1)} className="w-7 h-7 flex items-center justify-center text-[#676664]/40 hover:text-[#282828] hover:bg-[#282828]/5 rounded transition-colors mr-2" data-testid="back-btn" aria-label="Go back">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <a href="/" className="hover:text-[#9A7B4F]">Home</a>
          {breadcrumb ? (
            <>
              {" / "}<a href={breadcrumb.parent.url} className="hover:text-[#9A7B4F]">{breadcrumb.parent.name}</a>
              {breadcrumb.child && (
                <>{" / "}<a href={breadcrumb.child.url} className="hover:text-[#9A7B4F]">{breadcrumb.child.name}</a></>
              )}
            </>
          ) : (
            <>{" / "}<a href="/catalog" className="hover:text-[#9A7B4F]">Collection</a></>
          )}
          {" / "}<span className="text-[#676664]/60">{outfit.name}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-0 lg:gap-16">
          {/* Image/Video Gallery */}
          <div className="animate-fadeIn max-w-full min-w-0">
            <div className="relative w-full bg-white" style={{ overflow: 'hidden', height: '0', paddingBottom: '133.33%', maxWidth: '100%' }}>
              {allMedia[currentImageIndex]?.type === 'video' ? (
                isYouTubeUrl(allMedia[currentImageIndex].url) ? (
                  <iframe src={getYouTubeEmbedUrl(allMedia[currentImageIndex].url)} className="absolute inset-0 w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                ) : (
                  <video src={allMedia[currentImageIndex].url} controls className="absolute inset-0 w-full h-full object-contain bg-black" />
                )
              ) : (
                <img src={allMedia[currentImageIndex]?.url || images[0]} alt={outfitAlt} className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: `${(outfit.image_focal_points || {})[allMedia[currentImageIndex]?.url]?.x ?? 50}% ${(outfit.image_focal_points || {})[allMedia[currentImageIndex]?.url]?.y ?? 50}%` }} onError={(e) => { e.target.onerror=null; e.target.src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 800 1000' fill='%23F5F0E8'%3E%3Crect width='800' height='1000'/%3E%3Ctext x='400' y='500' text-anchor='middle' fill='%239A7B4F' font-size='18' opacity='0.4'%3EImage uploading soon%3C/text%3E%3C/svg%3E"; }} />
              )}
              {allMedia.length > 1 && (
                <>
                  <button onClick={() => setCurrentImageIndex(i => (i - 1 + allMedia.length) % allMedia.length)} className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/80 flex items-center justify-center hover:bg-white z-10"><ChevronLeft className="w-4 h-4 text-[#676664]" strokeWidth={1.5} /></button>
                  <button onClick={() => setCurrentImageIndex(i => (i + 1) % allMedia.length)} className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/80 flex items-center justify-center hover:bg-white z-10"><ChevronRight className="w-4 h-4 text-[#676664]" strokeWidth={1.5} /></button>
                </>
              )}
              <SaveHeart outfitId={id} className="absolute top-3 right-3 w-9 h-9 bg-white/80 hover:bg-white flex items-center justify-center z-10" size={16} />
            </div>
            {allMedia.length > 1 && (
              <div className="flex gap-1.5 overflow-x-auto pt-2 pb-1">
                {allMedia.map((media, index) => (
                  <button key={index} onClick={() => setCurrentImageIndex(index)} className={`flex-shrink-0 w-16 h-20 overflow-hidden ${index === currentImageIndex ? "opacity-100 ring-2 ring-[#9A7B4F]" : "opacity-40 hover:opacity-70"}`}>
                    {media.type === 'video' ? (
                      <div className="w-full h-full bg-[#6B5B4F] flex items-center justify-center"><Play className="w-5 h-5 text-white" fill="white" /></div>
                    ) : (<img src={media.url} alt={`${outfit.name} view ${index + 1} – Indian outfit hire Melbourne`} className="w-full h-full object-cover" />)}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* === DETAILS COLUMN === */}
          <div className="pt-5 lg:pt-0 animate-fadeIn stagger-1 overflow-hidden min-w-0 max-w-full">

            {/* A. Product Name + Save */}
            <div className="flex items-start justify-between gap-3">
              <h1 className="text-2xl lg:text-[2.4rem] text-[#1A1A1A] leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }} data-testid="outfit-title">{outfit.name}</h1>
              <SaveHeart outfitId={id} className="mt-2 p-1" size={24} dark />
            </div>

            {/* B. Reviews (only if any) */}
            {reviews.count > 0 && (
              <div className="flex items-center gap-2 mt-2" data-testid="reviews-display">
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map(star => (
                    <Star key={star} className={`w-3.5 h-3.5 ${star <= Math.round(reviews.average_rating) ? 'fill-[#9A7B4F] text-[#9A7B4F]' : 'text-[#282828]/15'}`} strokeWidth={1.5} />
                  ))}
                </div>
                <span className="text-[13px] text-[#5A5A5A]">({reviews.count} review{reviews.count !== 1 ? 's' : ''})</span>
              </div>
            )}

            {/* C. Security Bond* */}
            <div className="py-2.5 mt-3 border-t border-b border-[#282828]/8" data-testid="bond-display">
              <p className="text-[15px]">
                <span className="text-[#5A5A5A]" style={{ fontFamily: "'Montserrat', sans-serif" }}>Security Bond*:</span>{" "}
                <span className="text-[#1A1A1A] font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>${getBondAmount()}</span>
                <span className="text-[13px] text-[#9A7B4F] ml-2">Fully Redeemable*</span>
              </p>
            </div>

            {/* D. Size & Colour */}
            <div className="flex items-center gap-5 text-[14px] text-[#5A5A5A] mt-3" data-testid="size-color-info">
              <span>Size: <span className="text-[#1A1A1A] font-semibold">{outfit.size}</span></span>
              <span className="text-[#282828]/15">|</span>
              <span>Colour: <span className="text-[#1A1A1A] font-semibold">{outfit.color}</span></span>
              {outfit.show_size_guide !== false && (
                <button onClick={() => setShowSizeGuide(true)} className="flex items-center gap-1 text-[13px] text-[#9A7B4F] underline underline-offset-2 hover:text-[#8a6b3f] transition-colors ml-auto" data-testid="size-guide-link">
                  <Ruler className="w-3.5 h-3.5" strokeWidth={1.5} />Size Guide
                </button>
              )}
            </div>

            {/* === COMPACT OPTIONS SECTION === */}
            <div className="mt-5 space-y-5">

              {/* E. Choose Your Set — Radio card style */}
              {outfit.rental_options?.length > 0 && (
                <div data-testid="rental-options-section" className="w-full max-w-full">
                  <h3 className="text-lg lg:text-xl text-[#1A1A1A] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>Choose Your Set</h3>
                  <div className="space-y-3">
                    {outfit.rental_options.map((option) => {
                      const isMultiMode = outfit.variation_selection_mode === "multiple";
                      const isSelected = isMultiMode
                        ? selectedRentalOptions.some(o => o.id === option.id)
                        : selectedRentalOption?.id === option.id;
                      
                      const handleClick = () => {
                        if (isMultiMode) {
                          setSelectedRentalOptions(prev => 
                            prev.some(o => o.id === option.id)
                              ? prev.filter(o => o.id !== option.id)
                              : [...prev, option]
                          );
                        } else {
                          setSelectedRentalOption(option);
                        }
                      };

                      const optionPrice = localRentalDays <= 4 
                        ? (option.price_4_day || option.price_per_day || 0) 
                        : (option.price_8_day || option.price_4_day || option.price_per_day || 0);

                      return (
                        <div
                          key={option.id}
                          onClick={handleClick}
                          className={`flex items-start gap-4 px-5 py-4 cursor-pointer border rounded transition-all duration-150 ${
                            isSelected
                              ? 'border-[#9A7B4F]/50 bg-[#F5F0E6]'
                              : 'border-[#282828]/8 bg-[#FAF8F0] hover:border-[#282828]/15'
                          }`}
                          data-testid={`rental-option-${option.id}`}
                        >
                          {isMultiMode ? (
                            <div className={`w-[20px] h-[20px] rounded border-[1.5px] flex-shrink-0 mt-0.5 flex items-center justify-center transition-colors duration-150 ${
                              isSelected ? 'border-[#9A7B4F] bg-[#9A7B4F]' : 'border-[#282828]/20'
                            }`}>
                              {isSelected && (
                                <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M2 6l3 3 5-5" />
                                </svg>
                              )}
                            </div>
                          ) : (
                            <div className={`w-[20px] h-[20px] rounded-full border-[1.5px] flex-shrink-0 mt-0.5 flex items-center justify-center transition-colors duration-150 ${
                              isSelected ? 'border-[#9A7B4F]' : 'border-[#282828]/20'
                            }`}>
                              {isSelected && <div className="w-[10px] h-[10px] rounded-full bg-[#9A7B4F]" />}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <span className="text-[15px] text-[#1A1A1A] font-semibold">{option.name}</span>
                              {optionPrice > 0 && <span className="text-[14px] text-[#9A7B4F] font-medium">${optionPrice}</span>}
                            </div>
                            {option.description && <p className="text-[13px] text-[#5A5A5A] mt-1">{option.description}</p>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {outfit.display_message && (
                    <p className="text-[12px] text-[#7A7A7A] mt-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", fontSize: "13px" }} data-testid="rental-options-message">
                      {outfit.display_message}
                    </p>
                  )}
                </div>
              )}

              {/* F. Rental Period / G. Delivery — Hidden when Build Look accessory mode with locked dates */}
              {isBuildLookAccessory && !showDateChange ? (
                /* Locked dates display for Build Look accessories */
                <div className="mt-1" data-testid="build-look-locked-dates">
                  <div className="border-l-2 border-[#9A7B4F]/30 bg-[#9A7B4F]/[0.04] p-4 rounded-r-lg">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-[11px] text-[#9A7B4F] tracking-[0.15em] uppercase" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, fontSize: '12px' }}>
                        Your Look Dates
                      </h4>
                      <button
                        onClick={() => {
                          if (window.confirm("Changing dates will apply to ALL items in your look. Continue?")) {
                            setShowDateChange(true);
                          }
                        }}
                        className="text-[10px] text-[#9A7B4F] underline underline-offset-2 hover:text-[#8B6B3F] transition-colors"
                        data-testid="change-dates-btn"
                      >
                        Change
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-[10px] text-[#676664]/50 font-light">Event Date</p>
                        <p className="text-[13px] text-[#282828] font-medium mt-0.5">
                          {eventDate ? format(eventDate, "EEE, MMM d") : buildLookEventDate}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] text-[#676664]/50 font-light">Rental Period</p>
                        <p className="text-[13px] text-[#282828] font-medium mt-0.5">{localRentalDays} days</p>
                      </div>
                      {dateInfo && (
                        <>
                          <div>
                            <p className="text-[10px] text-[#676664]/50 font-light">Delivery</p>
                            <p className="text-[13px] text-[#282828] font-medium mt-0.5">{format(new Date(dateInfo.delivery_date + "T00:00:00"), "EEE, MMM d")}</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-[#676664]/50 font-light">Return By</p>
                            <p className="text-[13px] text-[#282828] font-medium mt-0.5">
                              {dateInfo.return_start && dateInfo.return_end
                                ? `${format(new Date(dateInfo.return_start + "T00:00:00"), "d")}–${format(new Date(dateInfo.return_end + "T00:00:00"), "d MMM")}`
                                : format(new Date(dateInfo.return_by_date + "T00:00:00"), "EEE, MMM d")}
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <>
              {/* F. Rental Period — Softer buttons */}
              {(outfit.price_4_day > 0 || outfit.price_8_day > 0) && (
                <div data-testid="rental-period-section" className={lockedBooking ? 'relative' : ''}>
                  <h3 className="text-base lg:text-lg text-[#1A1A1A] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                    Rental Period
                    {lockedBooking && <span className="text-[10px] text-[#9A7B4F] ml-2 align-middle font-normal tracking-[0.08em] uppercase">Locked</span>}
                  </h3>
                  <div className="flex gap-3 w-full max-w-full">
                    {[4, 8].map(d => (
                      <button
                        key={d}
                        onClick={() => !lockedBooking && handleRentalDaysChange(d)}
                        disabled={!!lockedBooking}
                        className={`flex-1 min-w-0 py-2.5 text-[13px] rounded transition-all duration-150 ${
                          localRentalDays === d
                            ? 'bg-[#9A7B4F] text-white font-medium shadow-sm'
                            : 'bg-[#F5F0E6] text-[#5A5A5A] border border-[#282828]/8'
                        } ${lockedBooking ? 'cursor-not-allowed opacity-70' : 'hover:border-[#9A7B4F]/30'}`}
                        style={{ fontFamily: "'Montserrat', sans-serif" }}
                        data-testid={`duration-${d}-btn`}
                      >
                        {d} Days – ${getPriceForDuration(d)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* G. Delivery Method — Softer buttons */}
              <div data-testid="delivery-method-section">
                <h3 className="text-base lg:text-lg text-[#1A1A1A] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                  Delivery Method
                  {lockedBooking && <span className="text-[10px] text-[#9A7B4F] ml-2 align-middle font-normal tracking-[0.08em] uppercase">Locked</span>}
                </h3>
                <div className="flex flex-col sm:flex-row gap-3 w-full max-w-full">
                  <button
                    onClick={() => !lockedBooking && setDeliveryMethod("pickup")}
                    disabled={!!lockedBooking}
                    className={`flex-1 min-w-0 py-2.5 text-[11px] sm:text-[12px] rounded transition-all duration-150 ${
                      deliveryMethod === "pickup"
                        ? 'bg-[#9A7B4F] text-white font-medium shadow-sm'
                        : 'bg-[#F5F0E6] text-[#5A5A5A] border border-[#282828]/8'
                    } ${lockedBooking ? 'cursor-not-allowed opacity-70' : 'hover:border-[#9A7B4F]/30'}`}
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                    data-testid="delivery-pickup-btn"
                  >
                    <MapPin className="w-3.5 h-3.5 inline-block mr-1.5 -mt-0.5" strokeWidth={1.5} />
                    Free Pick Up
                  </button>
                  <button
                    onClick={() => { if (lockedBooking || isPickupOnlyDate) return; setDeliveryMethod("delivery"); }}
                    disabled={!!lockedBooking || isPickupOnlyDate}
                    aria-disabled={!!lockedBooking || isPickupOnlyDate}
                    title={isPickupOnlyDate ? "Delivery isn't available for same-day or next-day bookings." : undefined}
                    className={`flex-1 min-w-0 py-2.5 text-[11px] sm:text-[12px] rounded transition-all duration-150 ${
                      deliveryMethod === "delivery"
                        ? 'bg-[#9A7B4F] text-white font-medium shadow-sm'
                        : 'bg-[#F5F0E6] text-[#5A5A5A] border border-[#282828]/8'
                    } ${(lockedBooking || isPickupOnlyDate) ? 'cursor-not-allowed opacity-50' : 'hover:border-[#9A7B4F]/30'}`}
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                    data-testid="delivery-local-btn"
                  >
                    <Truck className="w-3.5 h-3.5 inline-block mr-1.5 -mt-0.5" strokeWidth={1.5} />
                    Local Delivery
                  </button>
                </div>
                {/* Section 5 Rev 2 — pickup-only notice for same-day / next-day event dates. */}
                {isPickupOnlyDate && (
                  <div
                    className="mt-3 p-3 rounded border border-[#E7CE9A] bg-[#FAF3E0] text-[12px] text-[#3D2B1F] leading-relaxed"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                    data-testid="pickup-only-banner"
                  >
                    <strong className="font-semibold">Pickup only:</strong> Same-day and next-day bookings are collected in-store — delivery isn&#39;t available for this event date.
                  </div>
                )}
              </div>
                </>
              )}
            </div>

            {/* H-I: Only show full calendar/delivery info when NOT in Build Look accessory locked mode */}
            {(!isBuildLookAccessory || showDateChange) && (
              <>
            {/* H. Delivery Info */}
            <p className="text-[13px] text-[#9A7B4F] leading-relaxed mt-4" style={{ fontFamily: "'Montserrat', sans-serif" }} data-testid="delivery-info-text">
              Select a delivery date 1–2 days prior to your event date. It will be sent so it arrives to you on or before your selected date.
            </p>
            {auspost_content_visible && (
              <p className="text-[10px] text-[#676664]/40 mt-1 italic" data-testid="return-label-note">
                Prepaid Australia Post return label included with every order
              </p>
            )}

            {/* I. Calendar — Select Event Date */}
            <div className="mt-4" data-testid="delivery-date-section">
              <h3 className="text-[12px] text-[#282828] font-medium tracking-[0.05em] uppercase mb-2">
                Select Your Event Date
                {lockedBooking && <span className="text-[10px] text-[#9A7B4F] ml-2 font-normal tracking-[0.08em]">LOCKED</span>}
              </h3>
              <div className={`rounded bg-[#F3EDE3] p-1.5 sm:p-3 overflow-hidden ${lockedBooking ? 'opacity-60 pointer-events-none' : ''}`}>
                <Calendar
                  mode="single"
                  selected={eventDate}
                  onSelect={(date) => handleEventDateSelect(date)}
                  month={currentMonth}
                  onMonthChange={setCurrentMonth}
                  disabled={isDateDisabled}
                  className="rounded-none border-0 w-full !bg-transparent !p-0"
                  modifiers={{ rentalRange: (date) => isInRentalRange(date) }}
                  modifiersClassNames={{ rentalRange: "bg-[#9A7B4F]/15 text-[#9A7B4F]" }}
                  classNames={{
                    months: "w-full",
                    month: "w-full space-y-2",
                    caption: "flex justify-center pt-0.5 relative items-center",
                    caption_label: "text-xs font-medium text-[#282828]",
                    table: "w-full border-collapse",
                    head_row: "flex w-full",
                    head_cell: "text-[#676664]/50 flex-1 font-normal text-[0.7rem] text-center",
                    row: "flex w-full mt-1",
                    cell: "flex-1 relative text-center text-xs focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-[#9A7B4F]/10 [&:has([aria-selected])]:rounded",
                    day: "h-7 w-full font-normal aria-selected:opacity-100 hover:bg-[#9A7B4F]/10 rounded transition-colors text-xs",
                    day_selected: "bg-[#9A7B4F] text-white hover:bg-[#8B6B3F]",
                    day_today: "bg-[#E8DDD0] text-[#676664]",
                    day_outside: "text-[#676664]/30",
                    day_disabled: "text-[#676664]/25 line-through",
                  }}
                />
              </div>
              <p className="text-[10px] text-[#5A5A5A] mt-2 leading-relaxed">
                Local delivery $15 within 15km of Oakleigh South. Select Post at checkout.
              </p>
            </div>

            {/* Rental Schedule (when date selected) */}
            {dateInfo && (
              <div className="border-l-2 border-[#9A7B4F]/30 bg-[#9A7B4F]/[0.04] p-4 mt-4" data-testid="rental-schedule">
                <h4 className="text-[11px] text-[#9A7B4F] tracking-[0.15em] uppercase mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, fontSize: '12px' }}>Rental Schedule</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div><p className="text-[10px] text-[#676664]/50 font-light">Delivery</p><p className="text-[13px] text-[#282828] font-medium mt-0.5">{format(new Date(dateInfo.delivery_date + "T00:00:00"), "EEE, MMM d")}</p></div>
                  <div><p className="text-[10px] text-[#676664]/50 font-light">Event</p><p className="text-[13px] text-[#282828] font-medium mt-0.5">{format(new Date(dateInfo.event_date + "T00:00:00"), "EEE, MMM d")}</p></div>
                  <div><p className="text-[10px] text-[#676664]/50 font-light">Duration</p><p className="text-[13px] text-[#282828] font-medium mt-0.5">{localRentalDays} days</p></div>
                  <div><p className="text-[10px] text-[#676664]/50 font-light">Return By</p><p className="text-[13px] text-[#282828] font-medium mt-0.5">{dateInfo.return_start && dateInfo.return_end ? `${format(new Date(dateInfo.return_start + "T00:00:00"), "d")}–${format(new Date(dateInfo.return_end + "T00:00:00"), "d MMM")}` : format(new Date(dateInfo.return_by_date + "T00:00:00"), "EEE, MMM d")}</p></div>
                </div>
              </div>
            )}

            {/* Show warning when date change is active in Build Look accessory mode */}
            {showDateChange && isBuildLookAccessory && (
              <div className="mt-2 px-3 py-2 bg-[#FDF6EC] border border-[#E8D5B5] rounded-lg">
                <p className="text-[11px] text-[#9A7B4F]">
                  Changing dates will update the date and rental period for all items in your look.
                </p>
                <button
                  onClick={() => setShowDateChange(false)}
                  className="text-[10px] text-[#676664] underline mt-1"
                  data-testid="cancel-date-change-btn"
                >
                  Cancel change
                </button>
              </div>
            )}
              </>
            )}

            {/* Build Your Look — Vibrant & Prominent - HIDE when in Build Look flow or coming from builder */}
            {!inBuildLookFlow && !fromBuildLook && !lookLoading && lookItems.length > 0 && (
              <div className="mt-5 bg-[#F5F0E6] border-2 border-[#9A7B4F]/25 p-5" data-testid="compact-build-look">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#9A7B4F]" />
                    <h3 className="text-[16px] text-[#282828] font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Build Your Look</h3>
                  </div>
                  <span className="bg-[#9A7B4F] text-white text-[10px] tracking-wider uppercase px-3 py-1 font-semibold" data-testid="btl-tier-badge">
                    {lookCost.discountPct > 0 ? `Save ${lookCost.discountPct}%` : 'Add items to save'}
                  </span>
                </div>
                <p className="text-[11px] text-[#676664] mb-4">Tap items to add them to your look and save with bundle pricing</p>
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2.5">
                  {lookItems.map(item => {
                    const isSelected = selectedLookIds.has(item.id);
                    const itemPrice = localRentalDays <= 4 ? (item.price_4_day || 0) : (item.price_8_day || item.price_4_day || 0);
                    return (
                      <button
                        key={item.id}
                        onClick={() => toggleLookItem(item)}
                        className={`text-left transition-all rounded overflow-hidden ${isSelected ? 'ring-2 ring-[#9A7B4F] ring-offset-2 ring-offset-[#F5F0E6] shadow-md' : 'hover:shadow-sm hover:ring-1 hover:ring-[#9A7B4F]/30'}`}
                        data-testid={`compact-btl-${item.id}`}
                      >
                        <div className="aspect-[3/4] bg-[#f5f3f0] overflow-hidden relative">
                          <img src={item.image || ""} alt={`${item.name} for rent in Melbourne – Indian outfit hire`} className="w-full h-full object-cover" loading="lazy" />
                          {isSelected && (
                            <div className="absolute top-1 right-1 w-5 h-5 bg-[#9A7B4F] rounded-full flex items-center justify-center shadow">
                              <Check className="w-3 h-3 text-white" strokeWidth={2.5} />
                            </div>
                          )}
                        </div>
                        <div className="bg-white p-1.5">
                          <p className="text-[10px] text-[#282828] truncate font-medium">{item.name}</p>
                          <p className="text-[10px] text-[#9A7B4F] font-semibold">${itemPrice}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {/* Tier message + Rent the Look */}
                <div className="mt-4 pt-3 border-t border-[#9A7B4F]/15">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[11px] text-[#676664]">
                      {selectedLookIds.size > 0
                        ? <><span className="text-[#282828] font-medium">{selectedLookIds.size + 1} items</span>{lookCost.discountPct > 0 && <span className="text-[#9A7B4F] ml-1">· {lookCost.discountPct}% bundle discount</span>}</>
                        : <>2+ items: <span className="text-[#9A7B4F] font-medium">10% off</span> · 3: <span className="text-[#9A7B4F] font-medium">15% off</span> · 4+: <span className="text-[#9A7B4F] font-medium">20% off</span></>
                      }
                    </p>
                    <div className="flex gap-3">
                      {selectedLookIds.size > 0 && (
                        <button onClick={() => setSelectedLookIds(new Set())} className="text-[10px] text-[#676664] tracking-wider uppercase hover:underline" data-testid="btl-clear">Clear</button>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => { setSelectedLookIds(new Set(lookItems.map(i => i.id))); }}
                    className="w-full bg-[#282828] hover:bg-[#1a1a1a] text-white py-3 text-[11px] tracking-[0.15em] uppercase font-medium transition-colors"
                    data-testid="btl-rent-the-look"
                  >
                    Rent the Look{lookItems.length > 0 ? ` · ${lookItems.length} pieces` : ''}
                  </button>
                  {/* Build Your Own Look link */}
                  <button
                    onClick={() => navigate(`/build-your-look?addProduct=${id}`)}
                    className="w-full mt-2 py-2.5 text-[10px] tracking-[0.12em] uppercase text-[#9A7B4F] font-medium hover:text-[#8B6B3F] transition-colors flex items-center justify-center gap-1.5"
                    data-testid="build-your-own-look-link"
                  >
                    <span>Or Build Your Own Look</span>
                    <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            )}

            {/* J. Booking Summary - HIDE when in Build Look flow */}
            {!inBuildLookFlow && cost && (
              <div className="border-l-2 border-[#9A7B4F]/30 bg-[#9A7B4F]/[0.04] p-4 mt-4" data-testid="rental-summary">
                <h4 className="text-[11px] text-[#676664] tracking-[0.15em] uppercase mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, fontSize: '12px' }}>Booking Summary</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-[13px]"><span className="text-[#3A3A3A]">Rental Fee ({localRentalDays} days)</span><span className="text-[#282828] font-medium">${cost.rentalCost.toFixed(2)}</span></div>
                  {cost.promoDiscount > 0 && <div className="flex justify-between text-[13px]"><span className="text-[#9A7B4F]">Promotion</span><span className="text-[#9A7B4F]">-${cost.promoDiscount.toFixed(2)}</span></div>}
                  {lookCost.subtotal > 0 && (
                    <>
                      <div className="flex justify-between text-[13px]"><span className="text-[#3A3A3A]">Build Your Look ({selectedLookIds.size} items)</span><span className="text-[#282828] font-medium">${lookCost.subtotal.toFixed(2)}</span></div>
                      {lookCost.discount > 0 && <div className="flex justify-between text-[13px]"><span className="text-[#9A7B4F]">Bundle Discount ({lookCost.discountPct}%)</span><span className="text-[#9A7B4F]">-${lookCost.discount.toFixed(2)}</span></div>}
                    </>
                  )}
                  <div className="flex justify-between text-[13px]"><span className="text-[#3A3A3A]">Security Bond*</span><span className="text-[#282828] font-medium">${(cost.deposit + lookCost.bonds).toFixed(2)}</span></div>
                  {deliveryFee > 0 && <div className="flex justify-between text-[13px]"><span className="text-[#3A3A3A]">Delivery Fee</span><span className="text-[#282828] font-medium">${deliveryFee.toFixed(2)}</span></div>}
                  <div className="flex justify-between pt-3 mt-2 border-t border-[#282828]/10"><span className="text-[#282828] text-[14px] font-medium">Total</span><span className="text-[#9A7B4F] font-semibold text-[16px]">${combinedTotal.toFixed(2)}</span></div>
                  <p className="text-[9px] text-[#676664]/30 mt-2">*Fully refundable upon return in original condition</p>
                </div>
              </div>
            )}

            {/* L. CTA Button Area — ABOVE Complete the Look */}
            {inBuildLookFlow && targetSlot ? (
              /* Build Your Look mode - show "Add To Look" button */
              <div className="mt-6 space-y-2.5" data-testid="build-look-cta-area">
                <div className="bg-[#FDF6EC] border border-[#E8D5B5] rounded-lg p-3 mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />
                  <span className="text-[12px] text-[#9A7B4F]">Adding to: {targetSlot === "outfit" ? "Your Outfit" : targetSlot === "jewellery" ? "Jewellery" : targetSlot === "clutch" ? "Clutches & Potlis" : "Something Extra"}</span>
                </div>
                {/* If outfit slot, show date/duration selectors */}
                {targetSlot === "outfit" && (
                  <Button
                    onClick={eventDate ? handleAddToLook : () => setShowPicker(true)}
                    className="w-full bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white py-5 lg:py-6 text-[12px] tracking-[0.18em] uppercase rounded-none transition-all duration-200"
                    data-testid="add-to-look-btn"
                  >
                    {eventDate && cost ? `Add To Look – $${cost.rentalCost.toFixed(2)}` : "Select Date First"}
                  </Button>
                )}
                {/* For accessory slots, use locked dates from build look context */}
                {targetSlot !== "outfit" && (
                  <Button
                    onClick={handleAddToLook}
                    className="w-full bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white py-5 lg:py-6 text-[12px] tracking-[0.18em] uppercase rounded-none transition-all duration-200"
                    data-testid="add-to-look-btn"
                  >
                    Add To Look – ${cost ? cost.rentalCost.toFixed(2) : (outfit?.price_4_day || 0).toFixed(2)}
                  </Button>
                )}
                {/* Choose Something Else - navigates to the appropriate category */}
                <Button
                  onClick={() => {
                    const slot = LOOK_SLOTS[targetSlot];
                    navigate(slot?.route || "/build-your-look");
                  }}
                  variant="outline"
                  className="w-full border-[#282828]/15 text-[#676664] py-3 text-[10px] tracking-[0.12em] uppercase hover:border-[#9A7B4F]/30 rounded-none"
                  data-testid="choose-something-else-btn"
                >
                  Choose Something Else
                </Button>
                {/* Cancel - small X button */}
                <button
                  onClick={() => navigate("/build-your-look")}
                  className="flex items-center justify-center gap-1.5 mx-auto mt-1 text-[10px] text-[#676664]/60 hover:text-[#676664] transition-colors"
                  data-testid="cancel-build-look-btn"
                >
                  <X className="w-3 h-3" strokeWidth={1.5} />
                  <span>Cancel</span>
                </button>
              </div>
            ) : lockedBooking ? (
              /* Add-on product page (from "View More") */
              <div className="mt-6 space-y-2.5" data-testid="addon-cta-area">
                <Button
                  onClick={handleAddToCart}
                  className="w-full bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white py-5 lg:py-6 text-[12px] tracking-[0.18em] uppercase rounded-none transition-all duration-200"
                  data-testid="add-to-my-look-btn"
                >
                  {eventDate && cost ? `Add This To My Look – $${combinedTotal.toFixed(2)}` : "Add This To My Look"}
                </Button>
                <Button
                  onClick={() => navigate(`/catalog/${outfit.categories?.[0] || outfit.category || ''}`)}
                  variant="outline"
                  className="w-full border-[#282828]/15 text-[#676664] py-4 text-[10px] tracking-[0.12em] uppercase hover:border-[#9A7B4F]/30 rounded-none"
                  data-testid="explore-similar-btn"
                >
                  Explore Similar Styles
                </Button>
                {lockedBooking.parentOutfitId && (
                  <button
                    onClick={() => navigate(`/outfit/${lockedBooking.parentOutfitId}`)}
                    className="block w-full text-center text-[11px] text-[#9A7B4F] border-b border-[#9A7B4F]/20 pb-0.5 hover:border-[#9A7B4F] mx-auto mt-1"
                    data-testid="back-to-main-btn"
                  >
                    Back to Main Outfit
                  </button>
                )}
              </div>
            ) : (
              /* Main product page */
              <div className="mt-4 space-y-2.5">
                <Button
                  onClick={eventDate ? handleAddToCart : () => setShowPicker(true)}
                  className="w-full bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white py-4 lg:py-5 text-[12px] tracking-[0.18em] uppercase rounded-none transition-all duration-200"
                  data-testid="add-to-cart-btn"
                >
                  {!eventDate || !cost
                    ? "Select Date to Book"
                    : cartItemMatch
                    ? `Update Cart – $${combinedTotal.toFixed(2)}`
                    : `Add to Cart – $${combinedTotal.toFixed(2)}`}
                </Button>
                {cartItemMatch && (
                  <p className="text-[10px] text-[#9A7B4F] text-center">This item is already in your cart</p>
                )}
                {/* Replace or Add New dialog */}
                {showReplaceDialog && (
                  <div className="bg-[#FDF6EC] border border-[#E8D5B5] rounded-lg p-4 space-y-3" data-testid="replace-or-add-dialog">
                    <p className="text-[12px] text-[#282828] font-medium">You've changed the dates or rental period.</p>
                    <p className="text-[11px] text-[#5A5A5A]">
                      Would you like to replace the existing booking, or keep it and add this as a separate rental?
                    </p>
                    <div className="flex gap-2">
                      <Button
                        onClick={handleUpdateCart}
                        className="flex-1 bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white py-2.5 text-[10px] tracking-[0.12em] uppercase rounded-none"
                        data-testid="replace-booking-btn"
                      >
                        Replace Booking
                      </Button>
                      <Button
                        onClick={handleAddNew}
                        variant="outline"
                        className="flex-1 border-[#9A7B4F]/30 text-[#9A7B4F] py-2.5 text-[10px] tracking-[0.12em] uppercase rounded-none hover:bg-[#9A7B4F]/5"
                        data-testid="add-new-booking-btn"
                      >
                        Add as New Booking
                      </Button>
                    </div>
                    <button
                      onClick={() => setShowReplaceDialog(false)}
                      className="block w-full text-center text-[10px] text-[#676664]/60 hover:text-[#676664]"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* === COLLAPSIBLE SECTIONS === */}
            <div className="mt-8">
              {/* Description — Collapsible */}
              {outfit.description && (
                <CollapsibleSection title="Description">
                  <p className="text-[14px] text-[#3A3A3A] leading-[1.85]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}>{outfit.description}</p>
                  {outfit.rrp > 0 && (
                    <p className="text-[14px] text-[#5A5A5A] mt-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Retail Price: ${outfit.rrp.toLocaleString()}</p>
                  )}
                </CollapsibleSection>
              )}

              {/* Best Suited For — Collapsible */}
              {outfit.best_suited_for?.length > 0 && (
                <CollapsibleSection title="Best Suited For">
                  <div className="flex flex-wrap gap-x-5 gap-y-1.5">
                    {outfit.best_suited_for.map((item, idx) => (
                      <span key={idx} className="text-[13px] text-[#282828]/70"><span className="text-[#9A7B4F] mr-1.5">·</span>{item}</span>
                    ))}
                  </div>
                </CollapsibleSection>
              )}

              {/* Rental Terms — Collapsible */}
              {outfit.conditions && (
                <CollapsibleSection title="Rental Terms">
                  <p className="text-[13px] text-[#282828]/55 leading-relaxed">{outfit.conditions}</p>
                </CollapsibleSection>
              )}

              {/* Disclaimer — NOT Collapsible */}
              <div className="py-4 border-b border-[#282828]/8">
                <div className="flex items-start gap-2.5">
                  <Info className="w-3.5 h-3.5 text-[#9A7B4F] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div>
                    <h4 className="text-[13px] text-[#282828] mb-1 font-medium">Disclaimer</h4>
                    <p className="text-[12px] text-[#282828]/50 leading-relaxed">Our pieces come from real wardrobes, not large fashion houses with full-scale photoshoots. To help you visualise the magic, we've used a little AI muse to style and present each outfit beautifully.</p>
                    <p className="text-[12px] text-[#282828]/50 leading-relaxed mt-1">While the real garment closely reflects what you see, minor variations in tone or detailing may exist. If you'd like to see the exact piece before booking, we're always delighted to share authentic photos.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Customer Reviews */}
            <ReviewsTabs outfitId={resolvedOutfitId || id} productReviews={reviews} onRefreshProduct={fetchReviews} />
          </div>
        </div>

        {/* Frequently Bundled */}
        {featureToggles?.frequently_bundled_enabled !== false && (
          <div className="max-w-[1400px] mx-auto px-4 lg:px-10"><FrequentlyBundled outfitId={resolvedOutfitId || id} onAddBundle={handleAddBundle} /></div>
        )}
        {/* You May Also Like - Horizontal Carousel */}
        <div className="max-w-[1400px] mx-auto px-4 lg:px-10">
          <YouMayAlsoLikeCarousel outfitId={resolvedOutfitId || id} />
        </div>
        {/* Recently Viewed - Horizontal Carousel */}
        {featureToggles?.recently_viewed_enabled !== false && (
          <div className="max-w-[1400px] mx-auto px-4 lg:px-10 pb-8"><RecentlyViewedCarousel currentOutfitId={id} /></div>
        )}
      </div>

      <CTLQuickView
        outfit={quickViewOutfit}
        isOpen={!!quickViewOutfit}
        onClose={() => setQuickViewOutfit(null)}
        onAddBundle={handleAddBundle}
        parentOutfitId={id}
        bookingParams={{
          eventDate,
          returnDate: rentalEnd,
          duration: localRentalDays,
          deliveryMethod,
        }}
      />
      <UpsellPopup
        outfitId={resolvedOutfitId || id} isOpen={showUpsell}
        onClose={() => { setShowUpsell(false); const cost = calculateCost(); if (cost) { const bookingPayload = { outfitId: outfit.id, eventDate: formatLocalDate(eventDate), dateInfo, rentalDays: localRentalDays, rentalOptionId: selectedRentalOption?.id || null, rentalOptionName: selectedRentalOption?.name || null, deliveryMethod, deliveryFee, ...cost }; sessionStorage.setItem("bookingData", JSON.stringify(bookingPayload)); navigate(`/booking/${outfit.id}`); } }}
        onAddItems={(items) => { handleUpsellAdd(items); setShowUpsell(false); toast.success(`${items.length} item${items.length > 1 ? "s" : ""} added to your bundle`); }}
        existingBundleIds={bundleItems.map(b => b.id)}
      />
      <Footer />
      <SizeGuideModal open={showSizeGuide} onClose={() => setShowSizeGuide(false)} categoryId={outfit?.categories?.[0]} />

      {/* Sticky Mobile Book Bar */}
      {showStickyBar && outfit && (
        <div className="fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-white border-t border-[#282828]/10 px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]" data-testid="sticky-book-bar">
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-[13px] text-[#282828] font-medium truncate">{outfit.name}</p>
              <p className="text-[12px] text-[#9A7B4F]">
                {eventDate && cost ? `$${combinedTotal.toFixed(0)} · ${localRentalDays}-day rental` : `From $${outfit.price_4_day || outfit.price_per_day || 0}`}
              </p>
            </div>
            {inBuildLookFlow && targetSlot ? (
              <button
                onClick={targetSlot === "outfit" ? (eventDate ? handleAddToLook : () => setShowPicker(true)) : handleAddToLook}
                className="flex-shrink-0 bg-[#9A7B4F] text-white px-5 py-2.5 text-[11px] tracking-[0.12em] uppercase hover:bg-[#8B6B3F] transition-colors"
                data-testid="sticky-add-to-look-btn"
              >
                {targetSlot === "outfit" ? (eventDate ? "Add To Look" : "Select Date") : "Add To Look"}
              </button>
            ) : (
              <button
                onClick={eventDate ? handleAddToCart : () => setShowPicker(true)}
                className="flex-shrink-0 bg-[#9A7B4F] text-white px-5 py-2.5 text-[11px] tracking-[0.12em] uppercase hover:bg-[#8B6B3F] transition-colors"
                data-testid="sticky-add-to-cart-btn"
              >
                {eventDate && cost ? (cartItemMatch ? "Update Cart" : "Add to Cart") : "Select Date"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
